import { formatNumber } from '@casa/util';
import type { Rect } from '../../elements/flex_label.ts';
import { drawIcon, type IconDefinition } from '../../elements/icon.ts';
import type { FlowState } from '../../elements/flow.ts';
import type { Context } from '../../elements/layout_elements.ts';
import type { TileFont } from '../value/value_tile.ts';
import type { SolarIcons, SolarValues } from './solar_tile.ts';

/**
 * The solar tile's quiet cross (tiles spec SO-2): the legacy layout, panels at the top, the grid on
 * the left, the house on the right and the battery at the bottom, drawn with outline icons in tinted
 * rings, thin lines and small running dots. Colour means the source. It is laid out on a 320 × 300
 * design, scaled to fit the tile. A wider or taller tile spreads the arms, up to 520 × 380; beyond
 * that the cross is centred, so that a long, narrow tile does not make it tiny.
 */

/** The colour of each source; the house takes the tile's text colour. */
export const QUIET_COLORS = {
    solar: '#f4b740',
    grid: '#6fa8ff',
    battery: '#3ed6a0',
} as const;

/** Power in or out of the grid or battery that counts as nothing, as on the legacy tile. */
const IDLE_WATTS = 50;

/** Power along one arm: 0 when nothing flows; `towardsHub` when it flows from the arm's end to the middle. */
export interface QuietFlow {
    power: number;
    towardsHub: boolean;
}

export interface QuietSolarContent {
    pvText: string;
    loadText: string;
    gridText: string;
    /** "Importing · 233 V", "233 V", or empty. */
    gridDetail: string;
    socText: string;
    /** "Charging · 2400 W", "Idle", or empty. */
    batteryDetail: string;
    /** The battery arm is left out when the tile has no battery source. */
    hasBattery: boolean;
    /** The Font Awesome name of the battery icon, by its level. */
    batteryIcon: string;
    pv: QuietFlow;
    home: QuietFlow;
    grid: QuietFlow;
    battery: QuietFlow;
}

export interface QuietFlowStates {
    pv: FlowState;
    home: FlowState;
    grid: FlowState;
    battery: FlowState;
}

const watts = (value: number, locale: string | undefined) : string => `${formatNumber(value, '0', locale)} W`;

function batteryIconOf(soc: number | null) : string {
    if (soc === null || soc < 12.5) {
        return 'battery-empty';
    }

    if (soc < 37.5) {
        return 'battery-quarter';
    }

    if (soc < 62.5) {
        return 'battery-half';
    }

    return soc < 87.5 ? 'battery-three-quarters' : 'battery-full';
}

/** What the quiet cross shows for these values. Powers are whole watts (SO-1). */
export function quietSolarContent(values: SolarValues, hasBattery: boolean, locale?: string) : QuietSolarContent {
    const { load, pv, batteryCharge, grid, gridVoltage, batterySoc } = values;
    const isImporting = grid !== null && grid > IDLE_WATTS;
    const isExporting = grid !== null && grid < -IDLE_WATTS;
    const isDischarging = batteryCharge !== null && batteryCharge > IDLE_WATTS;
    const isCharging = batteryCharge !== null && batteryCharge < -IDLE_WATTS;
    const voltageText = gridVoltage === null ? '' : `${formatNumber(gridVoltage, '0', locale)} V`;
    const gridState = isImporting ? 'Importing' : isExporting ? 'Exporting' : '';
    let batteryDetail = '';

    if (isDischarging || isCharging) {
        batteryDetail = `${isCharging ? 'Charging' : 'Discharging'} · ${watts(Math.abs(batteryCharge), locale)}`;
    } else if (batteryCharge !== null) {
        batteryDetail = 'Idle';
    }

    return {
        pvText: pv === null ? '–' : watts(pv, locale),
        loadText: load === null ? '–' : watts(load, locale),
        gridText: grid === null ? '–' : watts(Math.abs(grid), locale),
        gridDetail: [gridState, voltageText].filter((part) => part !== '').join(' · '),
        socText: batterySoc === null ? '–' : `${formatNumber(batterySoc, '0', locale)}%`,
        batteryDetail,
        hasBattery,
        batteryIcon: batteryIconOf(batterySoc),
        pv: { power: pv !== null && pv > 0 ? pv : 0, towardsHub: true },
        home: { power: load !== null && load > 0 ? load : 0, towardsHub: false },
        grid: { power: isImporting || isExporting ? Math.abs(grid) : 0, towardsHub: isImporting },
        battery: { power: isDischarging || isCharging ? Math.abs(batteryCharge) : 0, towardsHub: isDischarging },
    };
}

/** Design units the dots run per second: faster with more power, on a log scale, so that 50 W and 5 kW both read. */
export function dotSpeed(power: number) : number {
    return power > 0 ? 10 + 25 * Math.log10(1 + power / 100) : 0;
}

const DESIGN_WIDTH = 320;
const DESIGN_HEIGHT = 300;
const MAX_SPREAD_WIDTH = 520;
const MAX_SPREAD_HEIGHT = 380;
/** From the edge to the grid's and the house's centres. */
const SIDE_INSET = 60;
const NODE_RADIUS = 28;
const HUB_RADIUS = 6;
const DOT_SPACING = 9;
const DOT_RADIUS = 1.8;
const VALUE_SIZE = 20;
const LABEL_SIZE = 12;
const MUTED_ALPHA = 0.6;
const TRACK_ALPHA = 0.1;

interface Point {
    x: number;
    y: number;
}

/** Moves a flow's dots on by the time since the last paint. */
function advance(state: FlowState, power: number, now: number) : void {
    if (state.lastPaint !== undefined) {
        state.offset = (state.offset + ((now - state.lastPaint) / 1000) * dotSpeed(power)) % (DOT_SPACING * 2);
    }

    state.lastPaint = now;
}

/**
 * An arm from the hub to a node: its track, and its dots while power flows. The dots are circles,
 * not a dash pattern of zero-length dashes with round caps, which engines draw differently.
 */
function drawArm(ctx: Context, hub: Point, node: Point, flow: QuietFlow, state: FlowState, color: string, textColor: string) : void {
    const dx = node.x - hub.x;
    const dy = node.y - hub.y;
    const length = Math.hypot(dx, dy);
    const ux = dx / length;
    const uy = dy / length;
    const near = { x: hub.x + ux * (HUB_RADIUS + 3), y: hub.y + uy * (HUB_RADIUS + 3) };
    const far = { x: node.x - ux * (NODE_RADIUS + 3), y: node.y - uy * (NODE_RADIUS + 3) };
    const [from, to] = flow.towardsHub ? [far, near] : [near, far];

    ctx.save();
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.globalAlpha = TRACK_ALPHA;
    ctx.strokeStyle = textColor;
    ctx.lineWidth = 2.2;
    ctx.stroke();

    if (flow.power > 0) {
        const span = Math.hypot(to.x - from.x, to.y - from.y);
        const stepX = (to.x - from.x) / span;
        const stepY = (to.y - from.y) / span;

        ctx.globalAlpha = 1;
        ctx.fillStyle = color;
        ctx.beginPath();

        for (let along = state.offset % DOT_SPACING; along <= span; along += DOT_SPACING) {
            const x = from.x + stepX * along;
            const y = from.y + stepY * along;

            ctx.moveTo(x + DOT_RADIUS, y);
            ctx.arc(x, y, DOT_RADIUS, 0, Math.PI * 2);
        }

        ctx.fill();
    }

    ctx.restore();
}

function drawNode(ctx: Context, center: Point, color: string, isActive: boolean, icon: IconDefinition | undefined) : void {
    ctx.save();
    ctx.beginPath();
    ctx.arc(center.x, center.y, NODE_RADIUS, 0, Math.PI * 2);
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = color;
    ctx.fill();
    ctx.globalAlpha = isActive ? 0.9 : 0.35;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();

    if (icon) {
        const size = 26;

        drawIcon(ctx, icon, { left: center.x - size / 2, top: center.y - size / 2, width: size, height: size }, color);
    }
}

function drawLabel(ctx: Context, text: string, x: number, baseline: number, size: number, align: CanvasTextAlign, color: string, font: TileFont, alpha = 1) : void {
    if (text === '') {
        return;
    }

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.font = `${size}px ${font.family}`;
    ctx.fillStyle = color;
    ctx.textAlign = align;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(text, x, baseline);
    ctx.restore();
}

/** Paints the quiet cross in `box` at time `now`, moving each flow's dots on first. */
export function drawQuietSolar(
    ctx: Context,
    box: Rect,
    content: QuietSolarContent,
    icons: SolarIcons,
    states: QuietFlowStates,
    now: number,
    font: TileFont,
    textColor: string,
) : void {
    const scale = Math.min(box.width / DESIGN_WIDTH, box.height / DESIGN_HEIGHT);

    if (scale <= 0) {
        return;
    }

    const width = Math.min(box.width / scale, MAX_SPREAD_WIDTH);
    const height = Math.min(box.height / scale, MAX_SPREAD_HEIGHT);
    const solar = { x: width / 2, y: 55 };
    const battery = { x: width / 2, y: height - 45 };
    const hub = { x: width / 2, y: (solar.y + battery.y) / 2 };
    const grid = { x: SIDE_INSET, y: hub.y };
    const home = { x: width - SIDE_INSET, y: hub.y };
    const besideX = hub.x + NODE_RADIUS + 14;

    advance(states.pv, content.pv.power, now);
    advance(states.home, content.home.power, now);
    advance(states.grid, content.grid.power, now);
    advance(states.battery, content.battery.power, now);

    ctx.save();
    ctx.translate(box.left + (box.width - width * scale) / 2, box.top + (box.height - height * scale) / 2);
    ctx.scale(scale, scale);

    drawArm(ctx, hub, solar, content.pv, states.pv, QUIET_COLORS.solar, textColor);
    drawArm(ctx, hub, grid, content.grid, states.grid, QUIET_COLORS.grid, textColor);
    drawArm(ctx, hub, home, content.home, states.home, textColor, textColor);

    if (content.hasBattery) {
        drawArm(ctx, hub, battery, content.battery, states.battery, QUIET_COLORS.battery, textColor);
    }

    ctx.beginPath();
    ctx.arc(hub.x, hub.y, HUB_RADIUS, 0, Math.PI * 2);
    ctx.strokeStyle = textColor;
    ctx.lineWidth = 2;
    ctx.stroke();

    drawNode(ctx, solar, QUIET_COLORS.solar, content.pv.power > 0, icons.pv);
    drawNode(ctx, grid, QUIET_COLORS.grid, content.grid.power > 0, icons.grid);
    drawNode(ctx, home, textColor, content.home.power > 0, icons.home);

    drawLabel(ctx, content.pvText, besideX, solar.y - 3, VALUE_SIZE, 'left', textColor, font);
    drawLabel(ctx, 'Solar', besideX, solar.y + 15, LABEL_SIZE, 'left', textColor, font, MUTED_ALPHA);
    drawLabel(ctx, content.gridText, grid.x, grid.y + NODE_RADIUS + 27, VALUE_SIZE, 'center', textColor, font);
    drawLabel(ctx, content.gridDetail, grid.x, grid.y + NODE_RADIUS + 45, LABEL_SIZE, 'center', textColor, font, MUTED_ALPHA);
    drawLabel(ctx, content.loadText, home.x, home.y + NODE_RADIUS + 27, VALUE_SIZE, 'center', textColor, font);
    drawLabel(ctx, 'House', home.x, home.y + NODE_RADIUS + 45, LABEL_SIZE, 'center', textColor, font, MUTED_ALPHA);

    if (content.hasBattery) {
        drawNode(ctx, battery, QUIET_COLORS.battery, content.battery.power > 0, icons.battery);
        drawLabel(ctx, content.socText, besideX, battery.y - 3, VALUE_SIZE, 'left', textColor, font);
        drawLabel(ctx, content.batteryDetail, besideX, battery.y + 15, LABEL_SIZE, 'left', textColor, font, MUTED_ALPHA);
    }

    ctx.restore();
}
